import type { ApiConfig, OptionItem, Options, Props, } from "./types"
import { get, isEqual, isFunction, omit, } from "lodash-es"

/**
 * 缓存选项存储的类型定义
 * 用于存储每个API请求的选项数据和加载状态
 */
type OptionsStore = Record<string, {
  options: Options
  loading: boolean
}>

/**
 * 生成API配置和参数的唯一标识符
 * 用于缓存选项数据的键值
 * @param apiConfig API配置对象
 * @param params 请求参数
 * @returns 基于API配置和参数生成的唯一标识符
 */
async function generateUniqueId(apiConfig: ApiConfig, params: any,) {
  const { api, config, } = apiConfig
  const encoder = new TextEncoder()
  const data = encoder.encode(api.toString() + JSON.stringify(config,) + JSON.stringify(params,),)
  const hashBuffer = await crypto.subtle.digest("SHA-256", data,)
  return Array.from(new Uint8Array(hashBuffer,),)
    .map(byte => byte.toString(16,).padStart(2, "0",),)
    .join("",)
}

// 全局选项存储对象，用于缓存API请求结果
const optionsStore = reactive<OptionsStore>({},)

/**
 * 自定义Hook，用于处理API选项数据的获取和格式化
 * @param props 组件属性，包含API配置、参数等
 * @returns 包含选项数据、加载状态和操作方法的对象
 */
export function useOptions<P, R,>(props: Props<P, R>,) {
  const { apiConfig, immediate, beforeFetch, afterFetch, cacheData, } = props
  const options = ref<unknown[]>([],)
  const loading = ref(false,)

  /**
   * 获取选项数据的异步函数
   * 根据配置决定是否使用缓存，并处理数据获取和转换
   */
  async function fetchOptions() {
    // 检查是否提供了API配置
    if (!apiConfig) {
      console.warn("请传入apiConfig",)
      return
    }

    // 检查API函数是否存在且为函数类型
    const { api, } = apiConfig || {}
    if (!api || !isFunction(api,)) {
      console.warn("请传入正确的api",)
      return
    }

    // 如果启用了缓存
    if (cacheData) {
      // 生成缓存键值
      const cacheKey = props.cacheKey || await generateUniqueId(apiConfig, props.params,)

      // 如果缓存中已存在该键值的数据
      if (optionsStore[cacheKey]) {
        // 监听缓存数据变化并同步到组件
        watch(() => optionsStore[cacheKey].options, (value,) => {
          options.value = value
        },)
        options.value = optionsStore[cacheKey].options
        return
      }

      // 初始化缓存数据
      optionsStore[cacheKey] = {
        options: [],
        loading: true,
      }
      loading.value = true

      // 获取数据
      const data = await api(props.params as P,)
      const formatData = await afterFetch?.(data,) || []
      options.value = optionsStore[cacheKey].options = formatData
      loading.value = optionsStore[cacheKey].loading = false

      // 监听数据变化
      watchEffect(async() => {
        options.value = optionsStore[cacheKey].options = await afterFetch?.(data,) || []
      },)
      return
    }

    // 不使用缓存的情况
    loading.value = true
    const result = await api(props.params as P,)
    watchEffect(async() => {
      options.value = await afterFetch?.(result,) || []
    },)
    loading.value = false
    options.value = await afterFetch?.(result,) || []
  }

  /**
   * 格式化选项数据的计算属性
   * 将原始数据转换为标准的选项格式
   */
  const formatOptions = computed(() => {
    const { apiConfig, } = props
    // 检查是否提供了API配置
    if (!apiConfig) {
      return []
    }

    const { config, } = apiConfig
    // 如果没有配置，假定为简单的一维数组
    if (!config) {
      // 没有配置说明为一维数组
      return (options.value as string[] | number[]).map(e => ({
        label: e,
        value: e,
        disabled: false,
      }),)
    }

    // 解构配置项
    const { label: labelField, value: valueField, children: childrenField, } = config

    /**
     * 递归转换数据结构
     * @param data 原始数据数组
     * @returns 转换后的选项数据数组
     */
    function transformData(data: OptionItem[],): OptionItem[] {
      return data.map((item,) => {
        const value = get(item, valueField,)
        const label = get(item, labelField,)

        // 如果没有子项配置
        if (!childrenField) {
          return {
            label,
            value,
            ...omit(item, [labelField, valueField,],),
          }
        }

        // 处理包含子项的数据
        return {
          ...omit(item, [labelField, valueField, childrenField,],),
          label,
          value,
          ...(item[childrenField]
            ? { children: transformData(item[childrenField],), }
            : {}),
        }
      },)
    }

    return transformData(options.value as OptionItem[],)
  },)

  // 标记是否为首次获取数据
  let firstFetch = true

  // 监听参数变化，当参数变化时重新获取数据
  watch(
    () => props.params,
    async(value, oldValue,) => {
      // 如果值没有变化且不是首次获取，则直接返回
      if (isEqual(value, oldValue,) && !firstFetch) {
        return
      }
      firstFetch = false
      options.value = []
      // 执行获取前的验证
      const valid = await beforeFetch?.(value as P,)
      if (!valid) {
        return
      }

      // 获取选项数据
      fetchOptions()
    },
    { deep: true, immediate, },
  )

  // 返回供组件使用的方法和数据
  return {
    options, // 原始选项数据
    loading, // 加载状态
    formatOptions, // 格式化后的选项数据
    fetchOptions, // 获取选项数据的方法
  }
}
